import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { TeamsApiAdapter, type TenantChild } from '../adapters/TeamsApiAdapter';
import { useAuthStore } from '@shared/stores/authStore';

const httpClient = createHttpClient(apiClientWH);
const teamsAdapter = new TeamsApiAdapter(httpClient);

interface UseTenantChildrenResult {
  tenantChildren: TenantChild[];
  isTenant3pl: boolean;
  isTenantExternal: boolean;
  isTenant: boolean;
  requiresTenantChildSelection: boolean;
  shouldFetchTenantChildren: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
}

export const useTenantChildren = (): UseTenantChildrenResult => {
  const team = useAuthStore((state) => state.team);
  const teamId = team?.id;
  const isTenant3pl = team?.type === 'tenant_3pl';
  const isTenantExternal = team?.type === 'tenant_external';
  const isTenant = team?.type === 'tenant';

  const requiresTenantChildSelection = isTenant3pl;
  const shouldFetchTenantChildren =
    isTenant3pl || isTenantExternal || isTenant;

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['tenant-children', teamId],
    queryFn: () => teamsAdapter.getTeamsChild(teamId),
    enabled: shouldFetchTenantChildren && Boolean(teamId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const tenantChildren = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    if (!teamId) {
      return data;
    }

    return data.filter(
      (child) => child.parent_team_id === teamId
    );
  }, [data, teamId]);

  return {
    tenantChildren,
    isTenant3pl,
    isTenantExternal,
    isTenant,
    requiresTenantChildSelection,
    shouldFetchTenantChildren,
    isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
  };
};
